// app/components/Plugins/Automations/nodes/implementations/BuySellNodeImpl.ts

import { BaseNode } from '../../core/BaseNode';
import { ExecutionContext } from '../../core/protocols';
import { AutomationNode, BuySellNodeData } from '../../types';

/**
 * Implementation of the Buy/Sell Node protocol
 * Responsible for executing trading actions based on signal conditions
 */
export class BuySellNodeImpl extends BaseNode {
  constructor(node: AutomationNode) {
    super(node);
  }

  /**
   * Process the buy/sell node
   * Executes trading actions based on the node configuration
   */
  async process(context: ExecutionContext): Promise<boolean> {
    // Get buy/sell configuration from node data
    const data = this.data as BuySellNodeData;
    const { action, quantity, orderType, price } = data;
    
    if (!action || !quantity) {
      context.addLog('Error: Incomplete buy/sell configuration');
      return false;
    }
    
    // Get the ticker from context
    const ticker = context.ticker;
    if (!ticker) {
      context.addLog('Error: No ticker specified in context');
      return false;
    }
    
    // Parse quantity (could be a fixed amount or a percentage)
    let parsedQuantity: number;
    if (quantity.endsWith('%')) {
      // Percentage of available funds/holdings
      const percentage = parseFloat(quantity) / 100;
      // In a real implementation, this would calculate based on account balance
      parsedQuantity = Math.floor(percentage * 1000); // Simulated calculation
    } else {
      // Fixed quantity
      parsedQuantity = parseFloat(quantity);
    }
    
    if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
      context.addLog(`Error: Invalid quantity ${quantity}`);
      return false;
    }
    
    // Log the action
    context.addLog(`Executing ${action} order for ${parsedQuantity} shares of ${ticker}`);
    
    // Create order details
    const order = {
      symbol: ticker,
      quantity: parsedQuantity,
      type: orderType || 'MARKET',
      side: action,
      price: price ? parseFloat(price) : undefined
    };
    
    // TODO: In a real implementation, this would submit the order to a trading API
    
    // Simulate order execution
    const executionPrice = action === 'BUY' ? 100.25 : 100.50; // Simulated prices
    const orderValue = parsedQuantity * executionPrice;
    
    // Log the result
    context.addLog(`${action} order executed at $${executionPrice.toFixed(2)} per share`);
    context.addLog(`Total order value: $${orderValue.toFixed(2)}`);
    
    // Store the order details in context data for other nodes to use
    context.data.lastOrder = {
      action,
      ticker,
      quantity: parsedQuantity,
      price: executionPrice,
      value: orderValue,
      timestamp: new Date().toISOString()
    };
    
    return true;
  }

  /**
   * Validate the buy/sell node configuration
   */
  validate() {
    const result = super.validate();
    const errors = [...result.errors];
    
    // Check if required fields are specified
    const data = this.data as BuySellNodeData;
    if (!data.action) {
      errors.push('No action (BUY/SELL) specified');
    }
    if (!data.quantity) {
      errors.push('No quantity specified');
    }
    
    // If order type is LIMIT, price is required
    if (data.orderType === 'LIMIT' && !data.price) {
      errors.push('Limit price is required for LIMIT orders');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get the input ports for this node
   * Buy/Sell node has one input
   */
  getInputs(): string[] {
    return ['in'];
  }

  /**
   * Get the output ports for this node
   * Buy/Sell node has one output
   */
  getOutputs(): string[] {
    return ['out'];
  }
}